import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Ban, RotateCcw } from "lucide-react";
import { fmtDate, PROGRAM_LABELS } from "@/lib/calendar";

export function ClassOccurrenceDialog({ ev, onClose, onSaved }) {
  const [f, setF] = useState({ title: "", start: "", end: "", note: "" });
  useEffect(() => { if (ev) setF({ title: ev.title, start: ev.start, end: ev.end, note: ev.notes || "" }); }, [ev]);
  if (!ev) return null;
  const put = async (cancelled, msg) => {
    try {
      await api.put(`/calendar/series/${ev.series_id}/override`, { date: ev.date, cancelled, ...f });
      toast.success(msg); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update class."); }
  };
  const reset = async () => { await api.delete(`/calendar/series/${ev.series_id}/override/${ev.date}`); toast.success("Class reset to normal schedule."); onSaved(); };
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const labels = Object.entries(ev.labels || {}).filter(([, v]) => v);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="mn max-w-md" data-testid="class-occurrence-dialog">
        <DialogHeader><DialogTitle className="text-xl font-extrabold">{fmtDate(ev.date, { weekday: "long", day: "numeric", month: "long" })}</DialogTitle></DialogHeader>
        <p className="text-xs text-[#6B6488]">Changes here apply to this date only. Shown to: {ev.programs.map((p) => PROGRAM_LABELS[p]).join(", ")}.</p>
        {labels.length > 0 && <p className="text-xs text-[#6B6488]">{labels.map(([p, l]) => `${PROGRAM_LABELS[p]} see "${l}"`).join(" · ")}</p>}
        {ev.cancelled && <div className="flex items-center gap-2 rounded-xl bg-rose-50 p-3 text-sm font-bold text-rose-600"><Ban className="h-4 w-4" /> Cancelled</div>}
        <div className="space-y-3">
          <div><Label>Title</Label><Input data-testid="occ-title-input" value={f.title} onChange={set("title")} className="mt-1.5 h-11 rounded-xl" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Start</Label><Input type="time" data-testid="occ-start-input" value={f.start} onChange={set("start")} className="mt-1.5 h-11 rounded-xl" /></div>
            <div><Label>End</Label><Input type="time" data-testid="occ-end-input" value={f.end} onChange={set("end")} className="mt-1.5 h-11 rounded-xl" /></div>
          </div>
          <div><Label>Note for students</Label><Input data-testid="occ-note-input" value={f.note} onChange={set("note")} placeholder="e.g. Moved due to school holidays" className="mt-1.5 h-11 rounded-xl" /></div>
        </div>
        <div className="flex flex-wrap gap-2 pt-2">
          <Button onClick={() => put(ev.cancelled, "Class updated.")} data-testid="occ-save-btn" className="rounded-xl ember-gradient font-bold">Save changes</Button>
          <Button variant="outline" onClick={() => put(!ev.cancelled, ev.cancelled ? "Class restored." : "Class cancelled.")} data-testid="occ-cancel-toggle"
            className={`rounded-xl ${ev.cancelled ? "" : "border-rose-200 text-rose-600 hover:bg-rose-50"}`}>{ev.cancelled ? "Restore class" : "Cancel this class"}</Button>
          {ev.overridden && <Button variant="ghost" onClick={reset} data-testid="occ-reset-btn" className="rounded-xl"><RotateCcw className="mr-1.5 h-4 w-4" /> Reset</Button>}
        </div>
      </DialogContent>
    </Dialog>
  );
}
